import * as XLSX from 'xlsx';
import { ExposureProperty } from '@/types/AccountExposure';
import { SafeReadCell } from '@/utilities/Worksheets';
import { z, ZodTypeAny } from 'zod';
import { IsNullOrUndefined } from '@/utilities/Helpers';

interface ExposurePropertyExt extends ExposureProperty {
  huDeductibleType: string;
  huDeductibleRawValue: number;
  eqDeductibleType: string;
  eqDeductibleRawValue: number;
  stDeductibleType: string;
  stDeductibleRawValue: number;
  ifDeductibleType: string;
  ifDeductibleRawValue: number;
  wfDeductibleType: string;
  wfDeductibleRawValue: number;
  wsDeductibleType: string;
  wsDeductibleRawValue: number;
}

const propertyColumnMap: Partial<Record<keyof ExposurePropertyExt, string>> = {
  locationName: 'A',
  userDefined5: 'B',
  userDefined4: 'C',
  userDefined3: 'D',
  renewables: 'E',
  address1: 'F',
  city: 'G',
  county: 'H',
  state: 'I',
  postalCode: 'J',
  latitude: 'K',
  longitude: 'L',
  buildingValue: 'M',
  otherPropertyValue: 'N',
  contentsValue: 'O',
  businessInterruptionValue: 'P',
  huDeductibleType: 'Q',
  huDeductibleRawValue: 'R',
  eqDeductibleType: 'S',
  eqDeductibleRawValue: 'T',
  stDeductibleType: 'U',
  stDeductibleRawValue: 'V',
  ifDeductibleType: 'W',
  ifDeductibleRawValue: 'X',
  wfDeductibleType: 'Y',
  wfDeductibleRawValue: 'Z',
  wsDeductibleType: 'AA',
  wsDeductibleRawValue: 'AB',
  construction: 'AC',
  occupancy: 'AD',
  yearBuilt: 'AE',
  stories: 'AF',
  squareFootage: 'AG',
  roofGeometry: 'AH',
  roofCovering: 'AI',
  roofAnchorage: 'AJ',
  roofYearBuilt: 'AK',
  windowProtection: 'AL',
  wallType: 'AM',
  wallSiding: 'AN',
  buildingShape: 'AO',
  foundationType: 'AP',
};

const TEMPLATE_LOCATION_SHEET_NAME = 'FILL_OUT_Location';


export function TransformWorksheetToProperties(sheetData: string, refData: Map<string, number>): { properties: ExposureProperty[], errors: string[] } {
  const workbook = XLSX.read(sheetData, { type: 'array' });

  // TODO: check to make sure the expected sheet is present.
  const worksheet = workbook.Sheets[TEMPLATE_LOCATION_SHEET_NAME];

  const properties: ExposureProperty[] = [];
  const errors: string[] = [];

  // range end rows minus start rows plus 1
  const range = XLSX.utils.decode_range(worksheet['!ref'] || '');
  const numberOfRows = range.e.r - range.s.r + 1;

  for (let i = 2; i < numberOfRows; i++) { // skip grouped header and header label row
    if (isRowEmpty(worksheet, i)) {
      break;
    }
    const result = transformRowToProperty(worksheet, i, refData);
    if (result.errors.length > 0) {
      errors.push(...result.errors);
    } else {
      properties.push(result.property as ExposureProperty);
    }
  }
  return { errors, properties: errors.length > 0 ? [] : properties };
}

function isRowEmpty(worksheet: XLSX.WorkSheet, row: number): boolean {
  for (const key in propertyColumnMap) {
    if (SafeReadCell(worksheet, `${propertyColumnMap[key as keyof ExposureProperty]}${row + 1}`, z.string().optional()).value) {
      return false;
    }
  }
  return true;
}

function valueForType(type: string | undefined, rawValue: number | undefined): { amount: (number | undefined), percent: number | undefined, validPercent: boolean | undefined } {
  switch (type) {
    case 'Dollar Amount':
      return { amount: rawValue, percent: undefined, validPercent: undefined };
    case 'Percentage Amount':
      return { amount: undefined, percent: rawValue, validPercent: !!(rawValue && rawValue <= 1 && rawValue >= 0) };
    default:
      return { amount: undefined, percent: undefined, validPercent: undefined };
  }
}

// ExposureProperty keys requiring translation to ID
const keysToMap = new Set<string>(['buildingShape', 'construction', 'foundationType', 'occupancy', 'renewables', 'roofAnchorage', 'roofCovering', 'roofGeometry', 'state', 'wallSiding', 'wallType', 'windowProtection']);

function transformRowToProperty(worksheet: XLSX.WorkSheet, row: number, refData: Map<string, number>): { property?: ExposureProperty, errors: string[] } {
  const property: Partial<ExposurePropertyExt> = {};
  const errors: string[] = [];

  const lookupVals = new Map<string, number>(); // holds string values the user has touched

  for (const key in propertyColumnMap) {
    const typedKey = key as keyof typeof propertyColumnMap;
    const cell = SafeReadCell(worksheet, `${propertyColumnMap[typedKey]}${row + 1}`, propertySchema[typedKey]);
    if (cell.error) {
      errors.push(cell.error);
    }
    const value = cell.value;
    property[typedKey] = !IsNullOrUndefined(value) ? value as never : undefined;

    if (keysToMap.has(typedKey)) {
      if (property[typedKey]) {
        const key = `${typedKey}--${property[typedKey]}`;
        if (refData.has(key)) {
          lookupVals.set(typedKey, <number>refData.get(key));
        } else {
          errors.push(`Invalid value <${property[typedKey]}> in cell ${propertyColumnMap[typedKey]}${row + 1}`);
        }
      }
    }
  }

  // set ID values for property values that need to be looked up
  property.buildingShapeId = lookupVals.has('buildingShape') ? lookupVals.get('buildingShape') : undefined;
  property.constructionId = lookupVals.has('construction') ? lookupVals.get('construction') : undefined;
  property.foundationTypeId = lookupVals.has('foundationType') ? lookupVals.get('foundationType') : undefined;
  property.occupancyId = lookupVals.has('occupancy') ? lookupVals.get('occupancy') : undefined;
  property.renewablesId = lookupVals.has('renewables') ? lookupVals.get('renewables') : undefined;
  property.roofAnchorageId = lookupVals.has('roofAnchorage') ? lookupVals.get('roofAnchorage') : undefined;
  property.roofCoveringId = lookupVals.has('roofCovering') ? lookupVals.get('roofCovering') : undefined;
  property.roofGeometryId = lookupVals.has('roofGeometry') ? lookupVals.get('roofGeometry') : undefined;
  property.stateId = lookupVals.has('state') ? lookupVals.get('state') : undefined;
  property.wallSidingId = lookupVals.has('wallSiding') ? lookupVals.get('wallSiding') : undefined;
  property.wallTypeId = lookupVals.has('wallType') ? lookupVals.get('wallType') : undefined;
  property.windowProtectionId = lookupVals.has('windowProtection') ? lookupVals.get('windowProtection') : undefined;

  // set correct value for amount of percentage for deductible properties
  const hu = valueForType(property.huDeductibleType, property.huDeductibleRawValue);
  property.huDeductibleAmount = hu.amount;
  property.huDeductiblePercent = hu.percent;

  const eq = valueForType(property.eqDeductibleType, property.eqDeductibleRawValue);
  property.eqDeductibleAmount = eq.amount;
  property.eqDeductiblePercent = eq.percent;

  const st = valueForType(property.stDeductibleType, property.stDeductibleRawValue);
  property.stDeductibleAmount = st.amount;
  property.stDeductiblePercent = st.percent;

  const iff = valueForType(property.ifDeductibleType, property.ifDeductibleRawValue);
  property.ifDeductibleAmount = iff.amount;
  property.ifDeductiblePercent = iff.percent;

  const wf = valueForType(property.wfDeductibleType, property.wfDeductibleRawValue);
  property.wfDeductibleAmount = wf.amount;
  property.wfDeductiblePercent = wf.percent;

  const ws = valueForType(property.wsDeductibleType, property.wsDeductibleRawValue);
  property.wsDeductibleAmount = ws.amount;
  property.wsDeductiblePercent = ws.percent;

  [{ cell: 'HU', ...hu }, { cell: 'EQ', ...eq }, { cell: 'ST', ...st }, { cell: 'IF', ...iff }, { cell: 'WF', ...wf }, { cell: 'WS', ...ws }].forEach(t => {
    if (t.validPercent === false) {
      errors.push(`Row ${row + 1}: ${t.cell} Deductible Percentage must be a decimal value between 0 and 1`);
    }
    if (t.amount && t.amount < 0) {
      errors.push(`Row ${row + 1}: ${t.cell} Deductible Amount cannot be negative`);
    }
  });

  const replacementValue = (property.buildingValue ?? 0) + (property.otherPropertyValue ?? 0) + (property.contentsValue ?? 0) + (property.businessInterruptionValue ?? 0);
  if (replacementValue <= 0) {
    errors.push(`Replacement Values do not sum to a positive number on row ${row + 1}`);
  }

  /* Address Validation
   *     lat+long
   *  OR street + city/county + state
   *  OR county + state
   *  OR ZIP
   */
  const postalCodePresent = !!property.postalCode;
  const latLongPresent = !!(property.latitude && property.longitude);
  const addressPresent = !!(property.address1 && (property.city || property.county) && property.state);
  const countyStatePresent = !!(property.county && property.state);

  const isLocationPresent = [postalCodePresent, latLongPresent, addressPresent, countyStatePresent].reduce((valid, fieldPresent) => (valid || fieldPresent), false);
  if (!isLocationPresent) {
    errors.push(`Address or geographic coordinates missing or incomplete on row ${row + 1}`);
  }

  // ensure lat/long is valid
  if ((property.latitude || property.longitude) && !(property.latitude && property.longitude)) {
    errors.push(`Geographic coordinates are incomplete on row ${row + 1}`);
  }

  // these year properties may have a zero value to indicate unknowns (stored as null in the backend)
  // year must be 0 _or_ (a 4 digit value _and_ not greater than the current year)
  if (property.yearBuilt && property.yearBuilt !== 0 && property.yearBuilt < 1000) {
    errors.push(`A valid 4 digit year is required on row ${row + 1}`)
  }

  if (property.roofYearBuilt && property.roofYearBuilt !== 0 && property.roofYearBuilt < 1000) {
    errors.push(`A valid 4 digit year is required on row ${row + 1}`)
  }

  if (errors.length > 0) {
    return { errors };
  }
  return { property: property, errors: [] } as never;
}

const currentYear = new Date().getFullYear();

const propertySchema: Record<keyof typeof propertyColumnMap, ZodTypeAny> = {
  locationName: z.string().max(100).optional(),
  userDefined5: z.string().max(255).optional(),
  userDefined4: z.string().max(255).optional(),
  userDefined3: z.string().max(255).optional(),
  renewables: z.string().optional(),

  address1: z.string().max(255).optional(),
  city: z.string().max(100).optional(),
  county: z.string().max(100).optional(),
  state: z.string().optional(),
  postalCode: z.string().max(12).optional(),

  latitude: z.coerce.number().gt(-91, 'Valid latitude range is -90 to 90').lt(91, 'Valid latitude range is -90 to 90').optional(),
  longitude: z.coerce.number().gt(-181, 'Valid longitude range is -180 to 180').lt(180, 'Valid longitude range is -180 to 180').optional(),

  buildingValue: z.coerce.number().min(0).lt(1e19, 'Number out of bounds').optional(),
  otherPropertyValue: z.coerce.number().min(0).lt(1e19, 'Number out of bounds').optional(),
  contentsValue: z.coerce.number().min(0).lt(1e19, 'Number out of bounds').optional(),
  businessInterruptionValue: z.coerce.number().min(0).lt(1e19, 'Number out of bounds').optional(),

  huDeductibleType: z.string().optional(),
  huDeductibleRawValue: z.coerce.number().lt(1e19, 'Number out of bounds').optional(),
  eqDeductibleType: z.string().optional(),
  eqDeductibleRawValue: z.coerce.number().lt(1e19, 'Number out of bounds').optional(),
  stDeductibleType: z.string().optional(),
  stDeductibleRawValue: z.coerce.number().lt(1e19, 'Number out of bounds').optional(),
  ifDeductibleType: z.string().optional(),
  ifDeductibleRawValue: z.coerce.number().lt(1e19, 'Number out of bounds').optional(),
  wfDeductibleType: z.string().optional(),
  wfDeductibleRawValue: z.coerce.number().lt(1e19, 'Number out of bounds').optional(),
  wsDeductibleType: z.string().optional(),
  wsDeductibleRawValue: z.coerce.number().lt(1e19, 'Number out of bounds').optional(),

  construction: z.string().optional(),
  occupancy: z.string().optional(),
  yearBuilt: z.coerce.number().int().gt(-1, 'The year must be a positive number').lt(currentYear + 1, 'The year must be less than or equal to the current year').optional(),
  stories: z.coerce.number().gt(-1, 'Stories must be a positive number').optional(),
  squareFootage: z.coerce.number().int().gt(-1, 'Square Footage must be a positive number').optional(),

  roofGeometry: z.string().optional(),
  roofCovering: z.string().optional(),
  roofAnchorage: z.string().optional(),
  roofYearBuilt: z.coerce.number().int().gt(-1, 'The year must be a positive number').lt(currentYear + 1, 'The year must be less than or equal to the current year').optional(),

  windowProtection: z.string().optional(),
  wallType: z.string().optional(),
  wallSiding: z.string().optional(),
  buildingShape: z.string().optional(),
  foundationType: z.string().optional(),
} as never;
